import { useEffect } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import LogoutAlert from "./LogoutAlert";
import {
  handleUserActiveStatus,
  logoutUser,
} from "../../redux/slices/authSlice";

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
  "visibilitychange",
];

const AppLogout = ({ children }) => {
  let timer;
  const dispatch = useDispatch();
  const isUserActive = useSelector((state) => state.auth.active);

  // this function sets the timer that logs out the user after 10 secs
  const handleLogoutTimer = () => {
    timer = setTimeout(() => {
      // clears any pending timer.
      resetTimer();
      // Listener clean up. Removes the existing event listener from the window
      Object.values(events).forEach((item) => {
        window.removeEventListener(item, resetTimer);
      });
      // logs out user
      handleTimeoutAlert();
    }, 900000);
  };

  // this resets the timer if it exists.
  const resetTimer = () => {
    if (timer) clearTimeout(timer);
  };

  // when component mounts, it adds an event listeners to the window
  // each time any of the event is triggered, i.e on mouse move, click, scroll, keypress etc, the timer to logout user after 10 secs of inactivity resets.
  // However, if none of the event is triggered within 10 secs, that is app is inactive, the app automatically logs out.
  useEffect(() => {
    let eventListenerCallbacks;
    eventListenerCallbacks = Object.values(events).map((item) => {
      const callback = () => {
        resetTimer();
        handleLogoutTimer();
      };
      if (isUserActive) {
        window.addEventListener(item, callback);
      } else {
        handleTimeoutAlert();
      }
      window.dispatchEvent(new Event("mousemove"));
      return { event: item, callback };
    });

    return () => {
      eventListenerCallbacks.forEach(({ event, callback }) => {
        window.removeEventListener(event, callback);
        clearTimeout(timer);
      });
    };
  }, [isUserActive]);

  const handleLogoff = () => {
    resetTimer();
    // Remove event listeners
    Object.values(events).forEach((item) => {
      window.removeEventListener(item, resetTimer);
      clearTimeout(timer);
    });
    dispatch(logoutUser());
    Swal.close();
    document.body.style.overflow = "auto";
    toast.success("Logged out successfully", { toastId: "timeout-toast" });
  };

  const handleStaySignedIn = () => {
    resetTimer();
    dispatch(handleUserActiveStatus(true));
    Swal.close();
  };

  // logs out user by clearing out auth token in localStorage and redirecting url to /signin page.
  const handleTimeoutAlert = async () => {
    dispatch(handleUserActiveStatus(false));
    withReactContent(Swal).fire({
      html: (
        <LogoutAlert
          handleLogoff={handleLogoff}
          handleStaySignedIn={handleStaySignedIn}
        />
      ),
      showLoaderOnConfirm: true,
      showConfirmButton: false,
      allowOutsideClick: false,
      width: "70%",
      customClass: {
        popup: "border-radius-0",
      },
    });
  };

  return children;
};

export default AppLogout;
